import { useState, useEffect } from "react";
import { db } from "../../firebase";
import usePremiumStatus from "./usePremiumStatus";
import { useAuth } from "../../Contexts/AuthContext";

// Subscriptions live under TWO roots, one per provider:
//   users-stripe/{uid}/subscriptions    - written by the Stripe extension
//   users-razorpay/{uid}/subscriptions  - written by our own Cloud Functions
// Reading only the Stripe root would leave every India (Razorpay) subscriber
// looking unsubscribed, because hasUnlimitedResumeAccess decides from these
// documents alone.
const SUBSCRIPTION_ROOTS = ["users-stripe", "users-razorpay"];

const readSubscriptions = async (root, uid) => {
  const snapshot = await db
    .collection(root)
    .doc(uid)
    .collection("subscriptions")
    .get();

  const subscriptions = [];
  snapshot.forEach((doc) => {
    const data = doc.data();
    if (data) {
      subscriptions.push({ subscriptionId: doc.id, data });
    }
  });

  return subscriptions;
};

const useFetchAllSubs = () => {
  const { currentUser } = useAuth();
  const uid = currentUser?.uid;
  const premiumStatusRole = usePremiumStatus(currentUser);

  const [isLoading, setIsLoading] = useState(false);
  const [allSubscriptions, setAllSubscriptions] = useState([]);
  // An empty list means "no subscription" and locks the user out of paid
  // features, so a read that FAILED must be distinguishable from one that
  // succeeded and found nothing - otherwise a rules change or a dropped
  // connection silently paywalls people who have already paid.
  const [hasLoadError, setHasLoadError] = useState(false);

  const getAllSubscriptions = async () => {
    if (!uid) return;

    setIsLoading(true);
    const results = await Promise.allSettled(
      SUBSCRIPTION_ROOTS.map((root) => readSubscriptions(root, uid))
    );

    const subscriptions = results
      .filter((result) => result.status === "fulfilled")
      .flatMap((result) => result.value);

    setAllSubscriptions(subscriptions);
    setHasLoadError(results.some((result) => result.status === "rejected"));
    setIsLoading(false);
  };

  useEffect(() => {
    getAllSubscriptions();
  }, [uid, premiumStatusRole]);

  return { isLoading, allSubscriptions, premiumStatusRole, hasLoadError };
};

export default useFetchAllSubs;
